import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, getSharedConstants } from '../baseQuery';
import type {
  ApiResponse,
  ApiResponseResult,
  AuthLoginResponseWithToken,
  OneTimeTokenLoginParams,
} from '../types';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['Auth', 'User'],
  endpoints: (builder) => ({
    oneTimeTokenLogin: builder.mutation<
      ApiResponse<AuthLoginResponseWithToken>,
      OneTimeTokenLoginParams
    >({
      query: (body) => ({
        url: 'api/auth/v1/one-time-token-login',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
      transformResponse: (
        response: ApiResponse<AuthLoginResponseWithToken>,
        meta: { response?: Response } | undefined
      ): ApiResponse<AuthLoginResponseWithToken> => {
        const { X_AUTH_HEADER } = getSharedConstants();
        const token = meta?.response?.headers?.get?.(X_AUTH_HEADER);
        const result = (response as Record<string, unknown>)['result'] as
          | ApiResponseResult
          | undefined;
        return {
          status: result || response.status,
          data: {
            ...response.data,
            token: token || '',
          },
        };
      },
    }),
    refreshToken: builder.mutation<ApiResponse<{ token: string }>, void>({
      query: () => ({
        url: 'api/auth/v1/token/refresh',
        method: 'POST',
      }),
      invalidatesTags: ['User', 'Auth'],
      transformResponse: (
        response: Record<string, unknown>,
        meta: { response?: Response } | undefined
      ): ApiResponse<{ token: string }> => {
        const { X_AUTH_HEADER } = getSharedConstants();
        const token = meta?.response?.headers?.get?.(X_AUTH_HEADER);
        return {
          status: (response.result || response.status) as ApiResponseResult,
          data: {
            ...(response.data as Record<string, unknown>),
            token: token || '',
          } as { token: string },
        };
      },
    }),
  }),
});

export const { useOneTimeTokenLoginMutation, useRefreshTokenMutation } =
  authApi;
